import { AttachmentDropzone, type AttachmentKind } from "@/components/recipes/attachment-dropzone";
import { FamilyBadge } from "@/components/recipes/family-badge";
import { MarkdownEditor } from "@/components/recipes/markdown-editor";
import { RelationsGraph } from "@/components/recipes/relations-graph";
import { TagChipInput } from "@/components/recipes/tag-chip-input";
import { TrainingConfigPanel } from "@/components/recipes/training-config-panel";
import { UnsavedPill } from "@/components/recipes/unsaved-pill";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { ErrorState } from "@/components/ui/error-state";
import { PageHeaderV2 } from "@/components/ui/page-header-v2";
import { TableSkeleton } from "@/components/ui/skeleton-v2";
import { cn } from "@/lib/cn";
import { formatBytes } from "@/lib/format";
import { useNavigate, useParams } from "@tanstack/react-router";
import { FileText, History, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { commitWithDirtyState } from "./commit-flow";
import { useRecipeEditor } from "./hooks";
import {
  useAddRelation,
  useCommitRecipe,
  useConfirmAttachmentUpload,
  useRemoveAttachment,
  useRemoveRelation,
  useRequestAttachmentUpload,
  useUpdateRecipe,
} from "./mutations";
import { useRecipe, useRecipeGraph, useRecipes } from "./queries";

export function RecipeDetailPage() {
  const { recipeId } = useParams({ from: "/recipes/$recipeId" });
  const navigate = useNavigate();
  const { data: recipe, isLoading, error } = useRecipe(recipeId);
  const { data: graph } = useRecipeGraph(recipeId, 1, "both");
  const update = useUpdateRecipe();
  const commit = useCommitRecipe();
  const requestUpload = useRequestAttachmentUpload();
  const confirmUpload = useConfirmAttachmentUpload();
  const removeAttachment = useRemoveAttachment();
  const addRelation = useAddRelation();
  const removeRelation = useRemoveRelation();
  const { data: allRecipes } = useRecipes(recipe?.project_id ?? null);

  const [attachmentKind, setAttachmentKind] = useState<AttachmentKind>("script");
  const [uploadingPath, setUploadingPath] = useState<string | null>(null);
  const [relationType, setRelationType] = useState<"prerequisite" | "followup" | "see_also">(
    "prerequisite",
  );
  const [relationTarget, setRelationTarget] = useState("");
  const [commitDialogOpen, setCommitDialogOpen] = useState(false);
  const [commitNotes, setCommitNotes] = useState("");

  const editor = useRecipeEditor(recipe);

  // Extract all tags from current project's recipes to provide suggestions
  const tagSuggestions: string[] = recipe?.tags || [];

  // Recipes available for relation (exclude self)
  const availableRecipes = (allRecipes?.items ?? []).filter((r) => r.id !== recipeId);

  if (error) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeaderV2 eyebrow="Knowledge" title="Recipe" />
        <ErrorState message={(error as Error).message} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  if (isLoading || !recipe) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeaderV2 eyebrow="Knowledge" title="Loading…" />
        <TableSkeleton rows={8} />
      </div>
    );
  }

  const nextVersion = (recipe.last_committed_version || 0) + 1;

  const handleDiscard = () => {
    if (!editor.isDirty) return;
    editor.reset();
    toast.success("Changes discarded");
  };

  const handleCommitClick = () => {
    setCommitNotes("");
    setCommitDialogOpen(true);
  };

  const handleCommitConfirm = async () => {
    if (!recipe) return;

    try {
      await commitWithDirtyState({
        isDirty: editor.isDirty,
        recipeId: recipe.id,
        currentEtag: editor.currentEtag,
        body: editor.body,
        tags: editor.tags,
        trainingConfig: editor.trainingConfig,
        commitNotes: commitNotes || null,
        updateMutateAsync: update.mutateAsync,
        commitMutateAsync: commit.mutateAsync,
      });
      setCommitDialogOpen(false);
    } catch {
      // Errors already toasted by mutations
    }
  };

  const handleVersionsClick = () => {
    navigate({ to: "/recipes/$recipeId/versions", params: { recipeId: recipe.id } });
  };

  const handleFileDrop = async (file: File, path: string, kind: AttachmentKind) => {
    setUploadingPath(path);

    try {
      // Request upload URL
      const uploadReq = await requestUpload.mutateAsync({
        recipeId: recipe.id,
        input: {
          expected_etag: editor.currentEtag,
          path,
          kind,
          size_bytes: file.size,
          description: null,
        },
      });

      if (!uploadReq?.upload_url) {
        throw new Error("No upload URL received");
      }

      // Upload file to S3
      const uploadRes = await fetch(uploadReq.upload_url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
      });

      if (!uploadRes.ok) {
        throw new Error(`Upload failed: ${uploadRes.statusText}`);
      }

      // Confirm upload
      await confirmUpload.mutateAsync({
        recipeId: recipe.id,
        input: {
          upload_token: uploadReq.upload_token,
        },
      });
    } catch (err) {
      toast.error(`Upload failed: ${(err as Error).message}`);
    } finally {
      setUploadingPath(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeaderV2
        eyebrow="Knowledge"
        title={recipe.name}
        meta={
          <div className="flex items-center gap-3">
            <FamilyBadge family={recipe.family || null} />
            <span className="font-mono text-sm text-muted">
              v{recipe.last_committed_version || "0"}
            </span>
            <UnsavedPill nextVersion={nextVersion} visible={editor.isDirty} />
          </div>
        }
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={handleVersionsClick}
              className="inline-flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-code-bg"
              type="button"
            >
              <History className="size-4" />
              Versions
            </button>
            {editor.isDirty && (
              <button
                onClick={handleDiscard}
                className="inline-flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-code-bg"
                type="button"
              >
                <X className="size-4" />
                Discard
              </button>
            )}
            <button
              onClick={handleCommitClick}
              disabled={commit.isPending}
              className="inline-flex items-center gap-2 rounded-md border border-line bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
              type="button"
            >
              <FileText className="size-4" />
              {commit.isPending ? "Committing…" : "Commit"}
            </button>
          </div>
        }
      />

      {/* Split layout: markdown on left, config/tags/relations/attachments on right */}
      <div className="grid grid-cols-[1.4fr_1fr] gap-6">
        {/* Left: Markdown editor */}
        <div className="rounded-lg border border-line bg-surface p-4">
          <h3 className="mb-3 font-mono text-xs uppercase tracking-wider text-muted">Body</h3>
          <div className="min-h-96">
            <MarkdownEditor value={editor.body} onChange={editor.setBody} readOnly={false} />
          </div>
        </div>

        {/* Right: Stacked panels */}
        <div className="flex flex-col gap-4">
          {/* Training Config */}
          {recipe.family && (
            <div className="rounded-lg border border-line bg-surface p-4">
              <h3 className="mb-3 font-mono text-xs uppercase tracking-wider text-muted">
                Training Config
              </h3>
              <TrainingConfigPanel
                family={recipe.family}
                config={editor.trainingConfig}
                onChange={editor.setTrainingConfig}
                readOnly={false}
              />
            </div>
          )}

          {/* Tags */}
          <div className="rounded-lg border border-line bg-surface p-4">
            <h3 className="mb-3 font-mono text-xs uppercase tracking-wider text-muted">Tags</h3>
            <TagChipInput
              tags={editor.tags}
              onChange={editor.setTags}
              suggestions={tagSuggestions}
            />
          </div>

          {/* Relations */}
          <div className="rounded-lg border border-line bg-surface p-4">
            <h3 className="mb-3 font-mono text-xs uppercase tracking-wider text-muted">
              Relations
            </h3>

            {/* Graph */}
            {graph?.edges && graph.edges.length > 0 && (
              <div className="mb-4">
                {/* biome-ignore lint/suspicious/noExplicitAny: type alignment between openapi and component */}
                <RelationsGraph graph={graph as any} />
              </div>
            )}

            {/* Existing relations list */}
            {recipe && (
              <div className="mb-4 space-y-1">
                {(["prerequisites", "followups", "see_also"] as const).map((type) => {
                  const rels = recipe[type] ?? [];
                  if (rels.length === 0) return null;
                  const label =
                    type === "prerequisites"
                      ? "prerequisite"
                      : type === "followups"
                        ? "followup"
                        : "see_also";
                  return (
                    <div key={type}>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-teal-600 dark:text-teal-400">
                        {label.replace("_", " ")}
                      </span>
                      {rels.map((rel) => (
                        <div
                          key={`${rel.to_recipe_id}-${label}`}
                          className="mt-1 flex items-center justify-between rounded border border-line bg-surface/50 px-3 py-1.5 text-xs"
                        >
                          <span className="font-mono text-ink">
                            {availableRecipes.find((r) => r.id === rel.to_recipe_id)?.name ??
                              rel.to_recipe_id.slice(0, 8)}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              removeRelation.mutate({
                                recipeId: recipe.id,
                                toRecipeId: rel.to_recipe_id,
                                relationType: label as "prerequisite" | "followup" | "see_also",
                                expectedEtag: editor.currentEtag,
                              })
                            }
                            className="text-rose-400 hover:text-rose-600"
                            aria-label="Remove relation"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add relation form */}
            {availableRecipes.length > 0 && (
              <div className="flex items-center gap-2">
                <select
                  value={relationType}
                  onChange={(e) => setRelationType(e.target.value as typeof relationType)}
                  className="rounded-md border border-line bg-surface px-2 py-1.5 font-mono text-[10px] uppercase text-ink"
                >
                  <option value="prerequisite">Prerequisite</option>
                  <option value="followup">Followup</option>
                  <option value="see_also">See also</option>
                </select>
                <select
                  value={relationTarget}
                  onChange={(e) => setRelationTarget(e.target.value)}
                  className="flex-1 rounded-md border border-line bg-surface px-2 py-1.5 text-xs text-ink"
                >
                  <option value="">Select recipe…</option>
                  {availableRecipes.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={!relationTarget || addRelation.isPending}
                  onClick={() => {
                    if (!relationTarget || !recipe) return;
                    addRelation.mutate(
                      {
                        recipeId: recipe.id,
                        input: {
                          expected_etag: editor.currentEtag,
                          to_recipe_id: relationTarget,
                          relation_type: relationType,
                        },
                      },
                      { onSuccess: () => setRelationTarget("") },
                    );
                  }}
                  className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
                >
                  Add
                </button>
              </div>
            )}

            {availableRecipes.length === 0 && !graph && (
              <p className="text-xs italic text-muted">No other recipes to relate to yet.</p>
            )}
          </div>

          {/* Attachments */}
          <div className="rounded-lg border border-line bg-surface p-4">
            <h3 className="mb-3 font-mono text-xs uppercase tracking-wider text-muted">
              Attachments
            </h3>

            {/* Existing attachments */}
            {recipe.attachments && recipe.attachments.length > 0 && (
              <div className="mb-4 space-y-2">
                {recipe.attachments.map((att) => (
                  <div
                    key={att.path}
                    className="flex items-center justify-between rounded border border-line bg-surface/50 px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-ink">{att.path}</span>
                      <span className="text-xs text-muted">({formatBytes(att.size)})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "rounded border px-2 py-0.5 text-[10px] uppercase",
                          att.kind === "script" &&
                            "border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300",
                          att.kind === "reference" &&
                            "border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300",
                          att.kind === "asset" &&
                            "border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300",
                        )}
                      >
                        {att.kind}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          removeAttachment.mutate({
                            recipeId: recipe.id,
                            expectedEtag: editor.currentEtag,
                            path: att.path,
                          })
                        }
                        className="text-rose-400 hover:text-rose-600 dark:hover:text-rose-300"
                        aria-label={`Remove ${att.path}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Upload new */}
            <AttachmentDropzone
              kind={attachmentKind}
              onKindChange={setAttachmentKind}
              onFileDrop={handleFileDrop}
            />

            {uploadingPath && <p className="mt-2 text-xs text-muted">Uploading {uploadingPath}…</p>}
          </div>
        </div>
      </div>

      {/* Commit dialog */}
      <Dialog open={commitDialogOpen} onOpenChange={setCommitDialogOpen}>
        <DialogContent>
          <DialogTitle>Commit version</DialogTitle>
          <DialogDescription>
            This will create version v{nextVersion}. Add optional notes describing what changed.
          </DialogDescription>
          <div className="mt-4">
            <textarea
              value={commitNotes}
              onChange={(e) => setCommitNotes(e.target.value)}
              placeholder="What changed in this version? (optional)"
              className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
              rows={3}
            />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <DialogClose asChild>
              <button
                type="button"
                className="rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-muted hover:bg-code-bg"
              >
                Cancel
              </button>
            </DialogClose>
            <button
              type="button"
              onClick={handleCommitConfirm}
              disabled={commit.isPending}
              className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:opacity-50"
            >
              {commit.isPending ? "Committing…" : `Commit v${nextVersion}`}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
